import Decimal from 'decimal.js';
import { IStowageDataAccessor } from 'src/types/ViewModel/Stowage/StowageRule/IStowageDataAccessor';
import { StowagePlan } from 'src/types/ViewModel/Stowage/StowagePlan';
import { CoatingCompatibility } from 'src/types/ViewModel/Stowage/Product/CoatingCompatibility';
import { UscgCompatibilitiesBM } from 'src/types/BusinessModel/Stowage/Product/UscgCompatibility/UscgCompatibilitiesBM';
import { IbcShipTypeEnum } from 'src/enums/stowage/ibcShipTypeEnum';
import { ProductCoatingCompatibility } from 'src/enums/stowage/stowageRule/productCoatingCompatibility';
import { PortCallStatusEnum } from 'src/enums/stowage/portCallStatusEnum';
import { TankContent } from 'src/types/ViewModel/Stowage/Manifest/TankContent';
import { RotationBerthCall } from 'src/types/ViewModel/Stowage/Rotation/RotationBerthCall';
import { RotationCargo } from 'src/types/ViewModel/Stowage/Rotation/RotationCargo';
import { RotationCargoOperation } from 'src/types/ViewModel/Stowage/Rotation/RotationCargoOperation';
import { RotationPortCall } from 'src/types/ViewModel/Stowage/Rotation/RotationPortCall';
import { StowageRuleContext } from 'src/types/ViewModel/Stowage/StowageRule/StowageRuleContext';
import { Tank } from 'src/types/ViewModel/Vessel/Tank';
import { truncateTo3Decimals } from 'src/utils/numberHelper';
import { getCargoOperationWeight, isManualCargoOperation } from 'src/utils/stowage/cargoOperationUtils';
import { isPositive } from 'src/utils/stowage/decimalsUtils';
import { getTankContentInBerthCall } from 'src/utils/stowage/manifest/cargoManifestUtils';
import { isTankBlocked } from 'src/utils/stowage/stowageOperation/blockedTankUtils';
import { notStowedWeightInTanks, stowedWeightInTanks } from 'src/utils/stowage/stowageOperation/stowageOperationUtils';
import { calculateVolume, getCorrectedLitreWeight } from 'src/utils/stowage/stowageOperation/volumeUtils';
import { isDeckTank } from 'src/utils/stowage/tankUtils';
import { areCargoUscgCompatible } from 'src/utils/stowage/uscgUtils';
import { getCargoTemperature } from 'src/utils/stowage/stowageOperation/cargoTemperatureUtils';
import { getNonCommingleableCargoes } from '../stowageOperation/commingleableCargoesUtils';

export class StowageDataAccessor implements IStowageDataAccessor {
    constructor(
        private readonly stowagePlan: StowagePlan,
        private readonly coatingCompatibilities: CoatingCompatibility[],
        private readonly uscgCompatibilities: UscgCompatibilitiesBM
    ) {}
    areTankContentsUscgCompatible = (tankContent: TankContent, adjacentTankContent: TankContent): boolean => {
        if (!tankContent?.cargoContents || !adjacentTankContent?.cargoContents) {
            return true; // No content means compatible
        }

        const tankContentsWithWeight = tankContent.cargoContents.filter((o) => isPositive(o.weight));
        const adjacentContentsWithWeight = adjacentTankContent.cargoContents.filter((o) => isPositive(o.weight));

        for (const content1 of tankContentsWithWeight) {
            for (const content2 of adjacentContentsWithWeight) {
                if (!areCargoUscgCompatible(this.uscgCompatibilities, content1.cargo, content2.cargo)) {
                    return false;
                }
            }
        }
        return true;
    };

    getCargoNominatedWeightUpToThisCall = (cargo: RotationCargo, berthCall: RotationBerthCall): number => {
        return (
            getCargoOperationWeight(this.stowagePlan, cargo, 'Loading', 'nominated', berthCall) -
            getCargoOperationWeight(this.stowagePlan, cargo, 'Unloading', 'nominated', berthCall)
        );
    };

    getCargoNominatedWeight = (cargo: RotationCargo): number => {
        return (
            getCargoOperationWeight(this.stowagePlan, cargo, 'Loading', 'nominated') -
            getCargoOperationWeight(this.stowagePlan, cargo, 'Unloading', 'nominated')
        );
    };

    getCargoOperationNominatedWeight = (cargoOperation: RotationCargoOperation): number => {
        return cargoOperation.nominatedWeight ?? 0;
    };

    getCargoStowedWeight = (cargo: RotationCargo): number => {
        return this.stowagePlan.stowageItems
            .reduce(
                (acc, si) => (si.cargoOperation.cargo.cargoGuid === cargo.cargoGuid && si.weight.gt(0) ? acc.plus(si.weight) : acc),
                new Decimal(0)
            )
            .toNumber();
    };

    getCargoTemperature = (cargo: RotationCargo, tank: Tank): number => {
        return getCargoTemperature(this.stowagePlan, cargo, tank);
    };

    getCargoOperationStowedWeight = (cargoOperation: RotationCargoOperation): number => {
        return truncateTo3Decimals(stowedWeightInTanks(this.stowagePlan, cargoOperation).abs().toNumber());
    };

    getCargoOperationUnstowedWeight = (cargoOperation: RotationCargoOperation): number => {
        return truncateTo3Decimals(notStowedWeightInTanks(this.stowagePlan, cargoOperation).abs().toNumber());
    };

    getCoatingCompatibility = (cargo: RotationCargo, tank: Tank): ProductCoatingCompatibility => {
        // Return NotAssessed if coating compatibilities are not loaded or cargo/tank is missing
        if (!this.coatingCompatibilities || !cargo || !tank) {
            return ProductCoatingCompatibility.NotAssessed;
        }

        // Coating resistance values are maintained at the default (category-level) product level, so the
        // category-level record must take precedence over a product-specific one — a sub-product's own
        // record can be stale/unmaintained once it has a default product for its category.
        let matchingCompatibility;
        if (cargo.product?.categoryId) {
            matchingCompatibility = this.coatingCompatibilities.find(
                (cc) => cc.coatingId === tank.coatingId && cc.categoryId === cargo.product.categoryId && cc.isCategoryLevel
            );
        }

        // Fall back to a product-specific record only if no category-level record exists for this coating.
        if (!matchingCompatibility) {
            matchingCompatibility = this.coatingCompatibilities.find(
                (cc) => cc.coatingId === tank.coatingId && cc.productId === cargo.productId && !cc.isCategoryLevel
            );
        }

        return matchingCompatibility?.compatibility ?? ProductCoatingCompatibility.NotAssessed;
    };
    getFillRatio = (tankContent: TankContent): number => {
        if (!tankContent?.tank?.volume || tankContent.tank.volume <= 0) {
            return 0;
        }
        return this.getTankContentVolume(tankContent) / tankContent.tank.volume;
    };

    getStowedWeight = (tankContent: TankContent): number => {
        return (tankContent?.cargoContents?.reduce((acc, c) => acc.plus(c.weight), new Decimal(0)) ?? new Decimal(0)).toNumber();
    };

    getTankContent = (stowageRuleContext: StowageRuleContext, tank: Tank): TankContent => {
        return getTankContentInBerthCall(this.stowagePlan, stowageRuleContext.berthCall, tank);
    };
    getTankContentLitreWeight = (tankContent: TankContent): number => {
        if (!tankContent?.cargoContents?.length) {
            return 0;
        }

        const cargoContent = tankContent.cargoContents[0];
        if (!cargoContent?.cargo) {
            return 0;
        }

        const litreWeight = getCorrectedLitreWeight(getCargoTemperature(this.stowagePlan, cargoContent.cargo, tankContent.tank), cargoContent.cargo);

        return litreWeight?.toNumber() ?? 0;
    };

    getTankContentVolume = (tankContent: TankContent): number => {
        return (
            tankContent?.cargoContents?.reduce(
                (acc, c) => acc.plus(calculateVolume(c.weight, c.cargo, getCargoTemperature(this.stowagePlan, c.cargo, tankContent.tank))),
                new Decimal(0)
            ) ?? new Decimal(0)
        ).toNumber();
    };

    getTankMaxTemperature = (tank: Tank): number => {
        if (tank.maxHeating !== undefined && tank.heatingCapacity !== undefined) {
            return Math.min(tank.maxHeating, tank.heatingCapacity);
        } else if (tank.maxHeating !== undefined) {
            return tank.maxHeating;
        } else if (tank.heatingCapacity !== undefined) {
            return tank.heatingCapacity;
        } else {
            return 0;
        }
    };
    hasIllegalCommingling = (tankContent: TankContent): boolean => {
        if (!tankContent?.cargoContents) {
            return false;
        }
        return getNonCommingleableCargoes(tankContent.cargoContents).length > 0;
    };

    isOtherCargoType1Or2Stowed = (cargo: RotationCargo, berthCall: RotationBerthCall): boolean => {
        return this.stowagePlan.stowageItems.some(
            (si) =>
                si.cargoOperation.cargo.cargoGuid !== cargo.cargoGuid &&
                si.cargoOperation.berthCall.berthCallGuid === berthCall.berthCallGuid &&
                (si.cargoOperation.cargo.product.category.shipType === IbcShipTypeEnum.One ||
                    si.cargoOperation.cargo.product.category.shipType === IbcShipTypeEnum.Two)
        );
    };

    isPortCallDeparted = (portCall: RotationPortCall): boolean => {
        return portCall.portCallStatus === PortCallStatusEnum.Departed || portCall.portCallStatus === PortCallStatusEnum.Hidden;
    };

    isTankBlocked = (berthCall: RotationBerthCall, tank: Tank): boolean => {
        return berthCall ? isTankBlocked(this.stowagePlan, tank, berthCall.portCall, berthCall, this.stowagePlan.rotation) : false;
    };

    isDeckTank = (tank: Tank): boolean => {
        return isDeckTank(tank);
    };

    isManualLoadingOrUnloadingOperation = (cargoOperation: RotationCargoOperation): boolean => {
        return isManualCargoOperation(cargoOperation);
    };

    isContractWeightEdited = (cargoOperation: RotationCargoOperation): boolean => {
        return cargoOperation.isContractWeightEdited === true;
    };
}
